/* WebComponentFloatingBox component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Method, Prop, State } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import FloatingBoxTemplate from './cwc-floating-box.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';
import 'intersection-observer';

/**
 * @slot close-icon - HTML element used as caption for the button to close the modal e.g. an img or svg
 */
@Component({
  tag: 'cwc-floating-box',
  styleUrl: './cwc-floating-box.scss',
  shadow: true,
})
export class FloatingBoxComponent {
  /**
   * properties
   */
  @Prop({ reflect: true }) opened: boolean = false;
  @Prop({ reflect: false }) name: string = 'CWCFloatingBox';
  @Prop({ reflect: true }) smartphone: boolean = false;
  @Prop() smartphoneBreakpoint: string = 'xs';
  @Prop() observableSelector: string = '';
  @Prop() closingEvent: string = '';

  private readonly storageEntryLifetime: number = 14; // days
  private readonly storageEntryLifetimeMS: number; // will be now in milliseconds
  private breakpointHandler: UtilBreakpoints = new UtilBreakpoints();
  private alreadyOpenedByObserver: boolean = false;

  private closingEventDelay: number = 10000; // milliseconds to wait till close

  @State() storageEntryExists: boolean;
  @State() isSmartphone: boolean;

  /**
   * element
   */
  @Element()
  element: HTMLElement;

  /**
   * Event thrown if the floating-box is reached
   *
   * @docs
   * @event on:floating-box:open
   */
  @Event({
    eventName: 'on:floating-box:open',
  })
  onFloatingBoxOpen: EventEmitter;

  /**
   * Event thrown if the floating-box is closed
   *
   * @docs
   * @event on:floating-box:close
   */
  @Event({
    eventName: 'on:floating-box:close',
  })
  onFloatingBoxClose: EventEmitter;

  /**
   * @constructor
   */
  constructor() {
    const now = new Date();
    this.storageEntryLifetimeMS = now.getTime();

    if (
      Number.parseInt(window.localStorage.getItem(this.getLocalStorageDismissedKey())) <
      this.storageEntryLifetimeMS - this.storageEntryLifetime * 24 * 60 * 60 * 1000 //eslint-disable-line no-magic-numbers
    ) {
      window.localStorage.removeItem(this.getLocalStorageDismissedKey());
    }
  }

  /**
   * Call this to close the floating-box
   *
   * @function close
   * @docs
   */
  @Method()
  async close(who?: string) {
    this.onClose(who);
  }

  onClose(who?: string) {
    /* set a storageEntry to avoid another opening if a name is set */
    if (this.name.length > 0) {
      window.localStorage.setItem(this.getLocalStorageDismissedKey(), '' + this.storageEntryLifetimeMS);
    }
    /* close the box */
    this.opened = false;

    /* emit the event */
    const emitData = {
      element: this.element,
      closingEvent: who && who === 'byClosingEvent' ? this.closingEvent : 'user-interact-closing-x',
    };

    this.onFloatingBoxClose.emit(emitData);
  }

  /**
   * Call this to open the floating-box
   *
   * @function close
   * @docs
   */
  @Method()
  async open(who?: string) {
    this.onOpen(who);
  }

  onOpen(who?: string) {
    /* check if it is already open */
    if (this.opened) {
      return;
    }
    this.opened = true;

    /* emit the event */
    const emitData = {
      element: this.element,
      observableSelector: who && who === 'byDefault' ? 'no-selector-shown-immediately' : this.observableSelector,
    };

    this.onFloatingBoxOpen.emit(emitData);
  }

  private getLocalStorageDismissedKey() {
    return this.name + '-dismissed';
  }

  /**
   * This observer manages the visibility of the button
   */
  intersectionObserver: IntersectionObserver;

  /**
   * initializeIntersectionObserver
   */
  initializeIntersectionObserver() {
    if (this.storageEntryExists) {
      this.alreadyOpenedByObserver = true;
    }

    // now create our intersection observer to observe the floating box
    this.intersectionObserver = new IntersectionObserver((observedElementList) => {
      if (observedElementList[0].intersectionRatio <= 0) return;
      if (!this.alreadyOpenedByObserver && !this.isSmartphone) {
        this.alreadyOpenedByObserver = true;
        this.onOpen();
      }
    }, {});

    this.intersectionObserver.observe(this.element.ownerDocument.querySelector(this.observableSelector));
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.storageEntryExists = window.localStorage.getItem(this.getLocalStorageDismissedKey()) ? true : false;
    this.isSmartphone = this.smartphone || this.breakpointHandler.breakpointDown(this.smartphoneBreakpoint);

    if (this.observableSelector == '' || this.element.ownerDocument.querySelector(this.observableSelector) === null) {
      if (!this.isSmartphone && !this.storageEntryExists) {
        this.onOpen('byDefault');
      }
    } else {
      this.initializeIntersectionObserver();
    }

    /* undecorated event listening | using the component fixed name | triggering first existence */
    this.element.ownerDocument.addEventListener(this.closingEvent, () => {
      setTimeout(() => {
        this.close('byClosingEvent');
      }, this.closingEventDelay);
    });
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    // this.element.style.display="inline";
    return <FloatingBoxTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(FloatingBoxComponent.prototype, AbstractComponent.prototype);

export interface FloatingBoxComponent extends AbstractComponent {}
